import { ref, computed, watch } from "vue"

/*
 * Single global focus timer instance. Persists state in localStorage so it
 * survives navigation between pages.
 */

const STORAGE_KEY = "focusflow.focusTimer"

interface PersistedTimer {
  endsAt: number | null
  durationMins: number
  running: boolean
}

function load(): PersistedTimer {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { endsAt: null, durationMins: 25, running: false }
}

function persist(state: PersistedTimer) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch { /* ignore */ }
}

const initial = load()
const endsAt = ref<number | null>(initial.endsAt)
const durationMins = ref<number>(initial.durationMins)
const running = ref<boolean>(initial.running)
const now = ref<number>(Date.now())

let interval: ReturnType<typeof setInterval> | null = null
function ensureTick() {
  if (interval) return
  interval = setInterval(() => { now.value = Date.now() }, 1000)
}
function stopTick() {
  if (interval) { clearInterval(interval); interval = null }
}

const remainingMs = computed(() => {
  if (!running.value || !endsAt.value) return durationMins.value * 60_000
  return Math.max(0, endsAt.value - now.value)
})

const display = computed(() => {
  const total = Math.ceil(remainingMs.value / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, "0")}`
})

watch([endsAt, durationMins, running], () => {
  persist({ endsAt: endsAt.value, durationMins: durationMins.value, running: running.value })
  if (running.value) ensureTick(); else stopTick()
})

// On page load: rehydrate the tick if the persisted state says we were
// running. Vue's `watch` above only fires on *changes*, so without this
// the interval never starts after a refresh and `now` never advances —
// the timer appears frozen.
if (running.value && endsAt.value) {
  if (endsAt.value <= Date.now()) {
    // The session finished while the page was reloading. Clean up so the
    // UI doesn't show a stale "running" state at 0:00.
    running.value = false
    endsAt.value = null
  } else {
    ensureTick()
  }
}

watch(remainingMs, (ms) => {
  if (running.value && ms <= 0) {
    running.value = false
    endsAt.value = null
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate([200, 100, 200])
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("Focus session complete", { body: `${durationMins.value} minutes done.` }) } catch { /* ignore */ }
    }
  }
})

export function useFocusTimer() {
  function start() {
    endsAt.value = Date.now() + durationMins.value * 60_000
    running.value = true
  }
  function pause() {
    if (!running.value || !endsAt.value) return
    durationMins.value = Math.max(1, Math.ceil((endsAt.value - Date.now()) / 60_000))
    endsAt.value = null
    running.value = false
  }
  function toggle() {
    if (running.value) pause()
    else start()
  }
  function reset() {
    running.value = false
    endsAt.value = null
  }
  function setDuration(mins: number) {
    durationMins.value = mins
    if (running.value) start()
  }
  return { running, durationMins, display, remainingMs, start, pause, toggle, reset, setDuration }
}
